import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function SignupConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    handleEmailConfirmation();
  }, []);

  const handleEmailConfirmation = async () => {
    try {
      // Get the token and type from URL parameters
      const token_hash = searchParams.get('token_hash');
      const token = searchParams.get('token');
      const type = searchParams.get('type');
      const access_token = searchParams.get('access_token');
      const refresh_token = searchParams.get('refresh_token');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for errors first
      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'An error occurred during email confirmation.');
        return;
      }

      // Handle different confirmation flows
      if (access_token && refresh_token) {
        // Direct token confirmation (newer Supabase flow)
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token,
          refresh_token
        });

        if (sessionError) {
          console.error('Session error:', sessionError);
          setStatus('error');
          setMessage('Failed to confirm email. The link may have expired or is invalid.');
          return;
        }

        if (data.user) {
          setStatus('success');
          setMessage('Thank you for confirming your email. You can now log in to your account.');
        }
        return;
      }

      // Fallback to OTP verification
      if (!token_hash && !token) {
        setStatus('error');
        setMessage('Invalid confirmation link. Please check your email and try again.');
        return;
      }

      try {
        const { data, error: confirmError } = await supabase.auth.verifyOtp({
          token_hash: token_hash || token || '',
          type: (type as any) || 'email'
        });

        if (confirmError) {
          console.error('Confirmation error:', confirmError);
          setStatus('error');
          setMessage('Failed to confirm email. The link may have expired or is invalid.');
          return;
        }

        if (data.user) {
          setStatus('success');
          setMessage('Thank you for confirming your email. You can now log in to your account.');
        } else {
          setStatus('error');
          setMessage('Email confirmation failed. Please try again.');
        }
      } catch (otpError) {
        console.error('OTP verification error:', otpError);
        setStatus('error');
        setMessage('Email confirmation failed. The link may have expired.');
      }
    } catch (error: any) {
      console.error('Email confirmation error:', error);
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background text-text relative">
      <div className="bubble"></div>
      <div className="bubble"></div>
      <div className="bubble"></div>
      <Navbar />
      
      <div className="max-w-md mx-auto px-4 py-32">
        <div className="bg-card-bg rounded-xl p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-4 text-white">
                Confirming Your Email
              </h1>
              <p className="text-gray-300">
                Please wait while we verify your email address...
              </p>
              <div className="mt-4 text-xs text-gray-400">
                <p>Current URL: {window.location.href}</p>
                <p>Token Hash: {searchParams.get('token_hash') ? 'Present' : 'Missing'}</p>
                <p>Access Token: {searchParams.get('access_token') ? 'Present' : 'Missing'}</p>
                <p>Type: {searchParams.get('type') || 'Missing'}</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-4 text-white">
                Email Confirmed!
              </h1>
              <p className="text-gray-300 mb-6">
                {message}
              </p>
              
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-white mb-4">Choose Your Sign In Path</h2>
                
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/customer/login')}
                    className="w-full gradient-border bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    🏢 Customer Login (Recruiters & Organizations)
                  </button>
                  
                  <button
                    onClick={() => navigate('/consultant/login')}
                    className="w-full gradient-border bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    👨‍💼 Workday SME's & Consultants Login
                  </button>
                  
                  <button
                    onClick={() => navigate('/admin/login')}
                    className="w-full bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    ⚙️ Admin Login
                  </button>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-4 text-white">
                Confirmation Failed
              </h1>
              <p className="text-gray-300 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/customer-signup')}
                  className="w-full gradient-border bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition-colors"
                >
                  Try Signing Up Again
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}